// =============================================================
// SCREENS 1 — Accueil / Mon profil / Match (Companion) — données réelles
// =============================================================
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Icon, SecHead, ChampImg, ItemsRow, SpellById, KDA, Meter, FormStrip,
  Kpi, LineChart, RankCrest, StatStrip, Spinner, ErrorState, EmptyState,
} from './components';
import {
  champLoading, champSplash, champCentered as champCenteredSplash, champSquare, getChamp, ddVersion, allChamps,
  getRunePerk, getRuneStyle, runeStyleIcon,
} from './ddragon';
import { useApi } from './useApi';
import { btrApi } from './api';
import MatchReplay, { isSummonersRift } from './MatchReplay';
import * as A from './adapters';
import { WINDOW_DAYS, fmtCompact, tierFr, laneLabel, regionName, platformCode, REGION_CODES } from './format';
import { RANGES, QUEUE_GROUPS, selectMatches, computeStats, fmtMinutes, fmtDateShort } from './profileStats';

// ============================================================
// HOME — Accueil
// ============================================================
function HomeScreen({ setRoute }) {
  const stats = useApi('home-stats', () => btrApi.siteStats());
  const champsState = useApi('home-champs', () => btrApi.champions({ days: WINDOW_DAYS, limit: 40, sort: 'winrate' }));
  const trends = useApi('home-trends', () => btrApi.metaTrends());
  const pros = useApi('home-players', () => btrApi.players({ limit: 7, sort: 'rank' }));

  const totalMatches = stats.data?.matches || 0;
  const champs = useMemo(
    () => A.adaptChampions(champsState.data || [], {}, totalMatches).filter(c => c.games >= 50),
    [champsState.data, totalMatches],
  );
  const feat = champs[0];
  const carousel = champs.slice(1, 5);

  return (
    <div className="anim-fade-in">
      {/* HERO */}
      <div className="container container-fluid" style={{padding:"24px 28px 0"}}>
        <section className="hero anim-fade-up">
          <div className="hero-bg">
            <img src={champSplash(feat?.id || "Jhin", 0)} alt=""/>
          </div>
          <div className="hero-content">
            <div className="eyebrow" style={{marginBottom:14}}>Patch {ddVersion()} · La méta du moment</div>
            <h1 className="h1" style={{color:"#fff"}}>Lis la Faille comme jamais.</h1>
            <p className="lead" style={{color:"rgba(255,255,255,0.78)", marginTop:18, maxWidth: 560}}>
              Profils, builds, statistiques et matchs — tout au même endroit, basé sur des
              millions de parties analysées.
            </p>
            <div className="row" style={{marginTop:28,gap:10}}>
              <button className="btn primary lg" onClick={() => window.dispatchEvent(new CustomEvent('btr:focus-search'))}>
                Chercher un invocateur <Icon name="arrowR" size={16}/>
              </button>
              <button className="btn outline lg" onClick={() => setRoute("build")} style={{color:"#fff",borderColor:"rgba(255,255,255,0.25)"}}>
                <Icon name="layers" size={16}/> Composer un build
              </button>
            </div>
          </div>
        </section>
      </div>

      <div className="container">
        {/* KPIs */}
        <section className="g-4 stagger kpis" style={{marginBottom:48}}>
          <Kpi label="Invocateurs analysés" value={stats.data ? fmtCompact(stats.data.players) : "…"}/>
          <Kpi label="Parties analysées" value={stats.data ? fmtCompact(stats.data.matches) : "…"}/>
          <Kpi label="Champions" value={String(allChamps().length)}/>
          <Kpi label="Patch courant" value={ddVersion()} accent/>
        </section>

        {/* TOP META */}
        <section style={{marginBottom: 56}}>
          <SecHead eyebrow="La méta du jour" title="Cinq champions à connaître"
                   meta={`Meilleurs winrates · ${WINDOW_DAYS} derniers jours`}
                   action={<button className="btn ghost" onClick={() => setRoute("champions")}>
                     Tout voir <Icon name="arrowR" size={14}/></button>}/>
          {champsState.loading && !champs.length ? <Spinner/> :
           champsState.error && !champs.length ? <ErrorState error={champsState.error} onRetry={champsState.refetch}/> :
           !feat ? <EmptyState title="Pas encore de données" hint="Relance le DataCollector pour alimenter la méta."/> : (
          <div style={{display:"grid",gridTemplateColumns:"1.4fr 1fr 1fr",gap:"var(--gap)"}} className="meta-grid stagger">
            <button className="featured-card" onClick={() => setRoute("champion", { champId: feat.id })}>
              <div className="featured-bg"><img src={champCenteredSplash(feat.id)} alt={feat.name}/></div>
              <div className="featured-content">
                <div className="tag accent" style={{marginBottom:14}}><Icon name="flame" size={11}/> Champion vedette</div>
                <h3 className="h2" style={{color:"#fff", fontSize:42}}>{feat.name}</h3>
                <p style={{color:"rgba(255,255,255,0.7)",fontSize:14,marginTop:6,fontStyle:"italic"}}>« {feat.title} »</p>
                <div className="row" style={{gap:24, marginTop:24}}>
                  <div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Winrate</div>
                    <div className="bignum" style={{fontSize:32,color:"var(--accent)"}}>{feat.winrate}%</div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Pickrate</div>
                    <div className="bignum" style={{fontSize:32,color:"#fff"}}>{feat.pickrate}%</div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",letterSpacing:"0.08em",textTransform:"uppercase"}}>Parties</div>
                    <div className="bignum" style={{fontSize:32,color:"#fff"}}>{fmtCompact(feat.games)}</div>
                  </div>
                </div>
              </div>
            </button>

            {carousel.map(c => (
              <button key={c.id} className="poster" onClick={() => setRoute("champion", { champId: c.id })}>
                <div className="poster-img">
                  <img src={champLoading(c.id)} alt={c.name} loading="lazy" decoding="async"
                       onError={e => e.target.src = champSquare(c.id)}/>
                </div>
                <div className="poster-tier"><span className={`tier tier-${c.tier}`}>{c.tier}</span></div>
                <div className="poster-info">
                  <div className="poster-name">{c.name}</div>
                  <div className="row" style={{gap:8,marginTop:6}}>
                    <span className="tag" style={{background:"rgba(255,255,255,0.12)",color:"#fff",backdropFilter:"blur(4px)"}}>WR {c.winrate}%</span>
                  </div>
                </div>
              </button>
            ))}
          </div>)}
          <style>{`
            .featured-card { position: relative; border-radius: 20px; overflow: hidden; padding: 0; text-align: left;
              border: 1px solid var(--border); transition: all .25s ease; cursor: pointer; grid-row: span 2; min-height: 340px; background: var(--surface); }
            .featured-card:hover { transform: translateY(-3px); border-color: var(--accent); box-shadow: 0 20px 50px rgba(0,0,0,0.4); }
            .featured-bg { position: absolute; inset: 0; z-index: 0; }
            .featured-bg img { width: 100%; height: 100%; object-fit: cover; transition: transform .8s ease; }
            .featured-card:hover .featured-bg img { transform: scale(1.04); }
            .featured-bg::after { content: ""; position: absolute; inset: 0;
              background: linear-gradient(180deg, transparent 0%, transparent 40%, rgba(11,12,16,0.95) 100%),
                          linear-gradient(90deg, rgba(11,12,16,0.6) 0%, transparent 50%); }
            .featured-content { position: absolute; left: 28px; right: 28px; bottom: 28px; z-index: 1; }
            @media (max-width: 980px) { .meta-grid { grid-template-columns: 1fr 1fr !important; } .featured-card { grid-column: 1 / -1; grid-row: auto; aspect-ratio: 16/9; min-height: 0; } }
            @media (max-width: 600px) { .meta-grid { grid-template-columns: 1fr !important; } }
          `}</style>
        </section>

        {/* Two-column: pros + trends */}
        <div className="g-2" style={{alignItems:"start", marginBottom: 56}}>
          <section>
            <SecHead eyebrow="Tableau d'honneur" title="Meilleurs invocateurs suivis"
                     meta="Classement Solo/Duo parmi les joueurs suivis par BuildTheRift"/>
            <div className="card card-flush stack-sm" style={{padding: 0}}>
              {pros.loading && !pros.data ? <div style={{padding:24}}><Spinner/></div> :
               (pros.data || []).slice(0,7).map((p,i) => {
                const solo = (p.ranked_stats || []).find(r => r.queue_type === 'RANKED_SOLO_5x5') || (p.ranked_stats || [])[0];
                return (
                <button key={p.puuid} className="pro-row"
                        onClick={() => setRoute("profile", { summoner: { region: p.region, name: p.game_name, tag: p.tag_line } })}>
                  <span className="pro-rank" style={{color: i===0?"var(--accent)":i<3?"var(--gold)":"var(--ink-3)"}}>{String(i+1).padStart(2,"0")}</span>
                  <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
                    <div style={{width:40,height:40,borderRadius:"50%",background:"var(--surface-3)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,color:"var(--accent)"}}>
                      {(p.game_name||"?").slice(0,2).toUpperCase()}
                    </div>
                    <div className="truncate">
                      <div style={{fontWeight:700, fontSize:14}}>{p.game_name}
                        <span style={{color:"var(--ink-3)",fontWeight:500,fontSize:13,marginLeft:6}}>#{p.tag_line}</span></div>
                      <div className="muted" style={{fontSize:12}}>
                        {platformCode(p.region)} · Niveau {p.summoner_level}{solo ? ` · ${solo.wins}V — ${solo.losses}D en classée` : ''}
                      </div>
                    </div>
                  </div>
                  {solo && <span className="tag accent" style={{fontSize:11,fontWeight:700}}>{tierFr(solo.tier)} {solo.rank} · {solo.league_points} LP</span>}
                </button>);
              })}
            </div>
          </section>

          <section>
            <SecHead eyebrow="Tendances" title="Ce qui monte"
                     action={<button className="btn ghost sm" onClick={() => setRoute("status")}>Status</button>}/>
            <div className="stack">
              {trends.loading && !trends.data ? <Spinner/> :
               (trends.data || []).filter(t => Number(t.winrate_diff) >= 1 && Number(t.recent_games) >= 500)
                 .sort((a,b) => Number(b.winrate_diff) - Number(a.winrate_diff)).slice(0,3).map((t) => {
                const dd = getChamp(Number(t.champion_id)) || { id: t.champion_name, name: t.champion_name };
                const up = Number(t.winrate_diff) >= 0;
                return (
                <button key={t.champion_id} className="card" style={{padding:20,display:"flex",gap:16,alignItems:"flex-start",textAlign:"left",width:"100%"}}
                        onClick={() => setRoute("champion", { champId: dd.id })}>
                  <ChampImg id={dd.id} size="sm"/>
                  <div className="grow">
                    <div className="h4">{dd.name} — {Number(t.recent_winrate).toFixed(1)}% WR</div>
                    <p className="muted" style={{fontSize:13,marginTop:4,marginBottom:0}}>
                      {fmtCompact(t.recent_games)} parties cette semaine · {Number(t.recent_winrate) >= 50 ? 'au-dessus de 50 % de victoires' : 'encore sous les 50 %'}.
                    </p>
                    <div className="mono" style={{fontSize:11,color: up?"var(--win)":"var(--loss)",marginTop:8}}>
                      {up ? "▲" : "▼"} {Math.abs(Number(t.winrate_diff)).toFixed(1)} pts vs semaine précédente
                    </div>
                  </div>
                </button>);
              })}
              {trends.data && trends.data.length === 0 && <EmptyState title="Pas de tendance" hint="Données récentes insuffisantes."/>}
            </div>
          </section>
        </div>

        {/* Modules */}
        <section>
          <SecHead eyebrow="Explorer" title="Toutes les fonctionnalités"/>
          <div className="g-3 stagger">
            {[
              { id:"profile", ico:"user", t:"Profil", d:"Historique, forme récente, champions favoris et maîtrises" },
              { id:"champions", ico:"layers", t:"Champions", d:"Tier list, winrates, matchups et tendances" },
              { id:"build", ico:"swords", t:"Builds", d:"Objets, runes et sorts les plus gagnants, par matchup" },
              { id:"status", ico:"globe", t:"Serveurs Riot", d:"Maintenances et incidents en direct, par région" },
              { id:"guesser", ico:"play", t:"Jeux", d:"Devine le champion ou l'objet, bats ton record" },
              { id:"arena", ico:"trophy", t:"Arena", d:"Challenge Top 1 : ta progression champion par champion" },
              { id:"clash", ico:"swords", t:"Clash", d:"Scoute l'équipe adverse avant la draft" },
              { href:"/tournament", ico:"flame", t:"Tournois", d:"Organise un tournoi Arena à 24 équipes, résultats automatiques" },
              { id:"companion", ico:"download", t:"Companion desktop", d:"Lit ton client : lobby Clash, partie en cours, Arena God" },
            ].map((m,i) => {
              const inner = <>
                <div style={{width:44,height:44,borderRadius:12,background:"var(--accent-soft)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--accent)",marginBottom:14}}>
                  <Icon name={m.ico} size={20}/>
                </div>
                <div className="h4">{m.t}</div>
                <p className="muted" style={{fontSize:13,marginTop:6,marginBottom:0,lineHeight:1.45}}>{m.d}</p>
                <div style={{marginTop:14,fontSize:13,color:"var(--accent)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>Ouvrir <Icon name="arrowR" size={14}/></div>
              </>;
              return m.href
                ? <Link key={m.t+i} to={m.href} className="module-card card card-hover">{inner}</Link>
                : <button key={m.t+i} className="module-card card card-hover" onClick={() => setRoute(m.id)}>{inner}</button>;
            })}
          </div>
          <style>{`.module-card { text-align: left; cursor: pointer; display: block; }`}</style>
        </section>
      </div>

      <style>{`
        .pro-row { display: flex; align-items: center; gap: 16px; padding: 12px 18px; background: var(--surface);
          border-bottom: 1px solid var(--border); text-align: left; width: 100%; font-family: var(--font-body); transition: background .12s; }
        .pro-row:hover { background: var(--surface-2); }
        .pro-row:first-child { border-top-left-radius: 16px; border-top-right-radius: 16px; }
        .pro-row:last-child { border-bottom: 0; border-bottom-left-radius: 16px; border-bottom-right-radius: 16px; }
        .pro-rank { font-family: var(--font-display); font-weight: 800; font-size: 18px; letter-spacing: -0.04em; width: 24px; }
      `}</style>
    </div>
  );
}

// ============================================================
// PROFILE — Mon profil
// ============================================================
// Page d'accueil du profil : recherche d'un Riot ID (aucun joueur n'est affiché par défaut).
function ProfileLanding({ setRoute, region, query }) {
  const [q, setQ] = useState(query ? `${query}#` : "");
  const [reg, setReg] = useState(region || 'EUW');
  const submit = (e) => {
    e.preventDefault();
    const t = q.trim();
    const i = t.indexOf("#");
    if (i <= 0 || !t.slice(i + 1).trim()) { setRoute("profile", { summoner: null, query: t.replace(/#.*$/, "") }); return; }
    setRoute("profile", { summoner: { region: reg, name: t.slice(0, i).trim(), tag: t.slice(i + 1).trim() }, query: null });
  };
  return (
    <div className="container anim-fade-in">
      <header style={{marginBottom: 28}}>
        <div className="eyebrow">Profil d'invocateur</div>
        <h1 className="h1" style={{marginTop:10}}>Quel <span style={{color:"var(--accent)"}}>invocateur</span> ?</h1>
        <p className="lead" style={{marginTop:14, maxWidth: 640}}>
          Historique des parties, forme récente, rangs, champions favoris et maîtrises. Tape un
          identifiant Riot complet : le nom, un dièse, puis le tag.
        </p>
      </header>
      <form className="card" style={{padding:24, maxWidth: 760}} onSubmit={submit}>
        <div className="row" style={{gap:12, flexWrap:"wrap"}}>
          <select className="nav-region" value={reg} onChange={(e) => setReg(e.target.value)} aria-label="Région">
            {REGION_CODES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <div className="field" style={{flex:"1 1 260px", padding:"12px 16px"}}>
            <Icon name="search" size={16} style={{color:"var(--ink-3)"}}/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom#TAG, par exemple Paul Atreides#SUS" autoFocus style={{fontSize:15}} aria-label="Riot ID"/>
          </div>
          <button className="btn primary lg" disabled={!q.trim()}>Voir le profil <Icon name="arrowR" size={16}/></button>
        </div>
        {query && (
          <p style={{marginTop:14, marginBottom:0, fontSize:13, color:"var(--loss)"}}>
            « {query} » n'a pas de tag. Un identifiant Riot s'écrit <strong>Nom#TAG</strong> : le tag est
            la partie affichée après le dièse dans ton client League of Legends (par exemple #EUW ou #1234).
          </p>
        )}
        <p className="muted" style={{marginTop:14, marginBottom:0, fontSize:12}}>
          Région sélectionnée : {regionName(reg)}. Tu peux la changer ici ou dans la barre du haut.
        </p>
      </form>
    </div>
  );
}

function ProfileScreen({ setRoute, nav, region }) {
  const [tab, setTab] = useState("overview");
  const [copied, setCopied] = useState(false);
  const [range, setRange] = useState('all');
  const [queueGroup, setQueueGroup] = useState('all');
  const [job, setJob] = useState(null); // chargement de fond de l'historique
  const forceRef = useRef(false);

  const target = nav?.summoner || null;
  const key = target ? `sum-${target.region}-${target.name}-${target.tag}` : null;
  const sumState = useApi(key, () => {
    const force = forceRef.current; forceRef.current = false;
    return btrApi.summoner({ region: target.region, name: target.name, tag: target.tag, refresh: force ? 1 : undefined });
  });
  const update = () => { forceRef.current = true; sumState.refetch(); };
  const newSearch = () => setRoute("profile", { summoner: null, query: null });

  const data = sumState.data;
  const puuid = data?.summoner?.puuid;
  const refetchRef = useRef(sumState.refetch); refetchRef.current = sumState.refetch;
  useEffect(() => { if (data?.history) setJob(data.history); }, [data]);
  // Tant que le job de fond tourne : statut toutes les 3 s, puis rechargement du profil
  useEffect(() => {
    if (!puuid || !job || (job.status !== 'running' && job.status !== 'queued')) return undefined;
    let alive = true;
    const id = setInterval(async () => {
      try {
        const st = await btrApi.summonerHistoryStatus(puuid);
        if (!alive) return;
        setJob((prev) => ({ ...prev, ...st }));
        if (st.status === 'done' || st.status === 'error') { clearInterval(id); if (st.status === 'done') refetchRef.current(); }
      } catch { /* on réessaie au prochain tick */ }
    }, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [puuid, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const extend = async (t) => {
    if (!puuid) return;
    try { const st = await btrApi.summonerHistoryExtend({ puuid, region: target.region, target: t }); setJob({ ...st, status: st.status || 'running', target: t }); }
    catch (e) { setJob({ status: 'error', error: e.message }); }
  };

  const s = useMemo(() => (data ? A.adaptSummoner(data) : null), [data]);
  const sel = useMemo(() => (s ? selectMatches(s.matches, range, queueGroup) : []), [s, range, queueGroup]);
  const st = useMemo(() => computeStats(sel), [sel]);

  if (!target) return <ProfileLanding setRoute={setRoute} region={region} query={nav?.query}/>;
  if (sumState.loading && !data) {
    return <div className="container"><Spinner label="Chargement du profil…"/></div>;
  }
  if (sumState.error && !data) {
    const notFound = sumState.error.status === 404;
    return (
      <div className="container">
        {notFound
          ? <EmptyState icon="user" title="Invocateur introuvable"
                        hint={`Aucun compte « ${target.name}#${target.tag} » sur ${regionName(target.region)}. Vérifie l'orthographe, le tag (après le dièse) et la région.`}/>
          : <ErrorState error={sumState.error} onRetry={sumState.refetch}/>}
        <div style={{textAlign:"center", marginTop:16}}>
          <button className="btn outline" onClick={newSearch}><Icon name="search" size={14}/> Nouvelle recherche</button>
        </div>
      </div>
    );
  }
  if (!s) return <ProfileLanding setRoute={setRoute} region={region} query={null}/>;

  const featChamp = st.byChamp?.[0]?.id || s.matches?.[0]?.champion || "Ahri";
  const mainRole = st.byRole?.[0];
  const open = (m) => setRoute("match", { matchId: m.id, summoner: { ...target, puuid: s.summoner.puuid } });
  const share = () => {
    const url = `${window.location.origin}${window.location.pathname}#profile/${target.region}/${encodeURIComponent(s.summoner.name)}/${encodeURIComponent(s.summoner.tag)}`;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(() => window.prompt('Lien du profil :', url));
    else window.prompt('Lien du profil :', url);
  };
  const total = s.matches.length;

  return (
    <div className="anim-fade-in">
      <div className="container container-fluid" style={{padding: "20px 28px 0"}}>
        <section className="hero" style={{minHeight: 380, padding: 36}}>
          <div className="hero-bg"><img src={champSplash(featChamp, 0)} alt=""/></div>
          <div className="hero-content" style={{maxWidth: 920}}>
            <div className="row" style={{gap:12,marginBottom:16}}>
              <span className="tag accent">{platformCode(s.summoner.region)}</span>
              <span className="tag" style={{background:"rgba(255,255,255,0.14)",color:"#fff"}}>Niveau {s.summoner.level ?? "—"}</span>
              <span className="tag" style={{background:"rgba(255,255,255,0.14)",color:"#fff"}}>{total} partie{total > 1 ? "s" : ""} analysée{total > 1 ? "s" : ""}</span>
            </div>
            <h1 className="h1" style={{color:"#fff"}}>{s.summoner.name}<span style={{color:"rgba(255,255,255,0.45)",fontWeight:600}}>#{s.summoner.tag}</span></h1>
            <p style={{color:"rgba(255,255,255,0.78)",fontSize:17,marginTop:14, maxWidth: 640,lineHeight:1.55}}>
              {st.games ? <>
                {st.byChamp?.[0] && <>Joue surtout <strong style={{color:"#fff"}}>{st.byChamp[0].name}</strong>{st.byChamp?.[1] && <> et <strong style={{color:"#fff"}}>{st.byChamp[1].name}</strong></>}. </>}
                Sur {st.games} partie{st.games > 1 ? "s" : ""}{st.oldest ? ` depuis le ${fmtDateShort(st.oldest)}` : ""} : <strong style={{color:"var(--win)"}}>{st.wins}V — {st.losses}D</strong> ({st.winrate} %), KDA <strong style={{color:"var(--accent)"}}>{st.kda}</strong>{st.kp != null && <>, {st.kp} % de participation aux kills</>}.
                {mainRole && <> Rôle principal : <strong style={{color:"#fff"}}>{mainRole.label}</strong> ({mainRole.pct} % des parties).</>}
              </> : <>Aucune partie récente trouvée pour ce compte{queueGroup !== 'all' ? " dans cette file" : ""}.</>}
            </p>
            <div className="row" style={{marginTop:24,gap:10}}>
              <button className="btn primary lg" onClick={update} disabled={sumState.loading}>
                <Icon name="refresh" size={16}/> {sumState.loading ? 'Mise à jour…' : 'Mettre à jour'}
              </button>
              <button className="btn outline lg" style={{color:"#fff",borderColor:"rgba(255,255,255,0.25)"}} onClick={share}>
                <Icon name={copied ? "check" : "share"} size={16}/> {copied ? "Lien copié !" : "Partager"}
              </button>
            </div>
          </div>
        </section>
      </div>

      <div className="container">
        <section className="g-3 stagger" style={{marginBottom: 20, marginTop: -24, position: "relative", zIndex: 2}}>
          <RankCard kind="Solo / Duo" data={s.solo}/>
          <RankCard kind="Flexible" data={s.flex}/>
          <div className="card">
            <div className="muted" style={{fontSize:12,fontWeight:600,marginBottom:10}}>Forme récente · {st.form.length} dernières</div>
            <FormStrip results={st.form.length ? st.form : [false]}/>
            <div className="row" style={{marginTop:14,gap:18,fontSize:13}}>
              <span className="muted">Série <strong style={{fontSize:16,marginLeft:4,color: st.streak.type === 'win' ? "var(--win)" : "var(--loss)"}}>{st.streak.n ? `${st.streak.n} ${st.streak.type === 'win' ? 'V' : 'D'}` : '—'}</strong></span>
              <span className="muted">Meilleure série <strong className="t-win" style={{fontSize:16,marginLeft:4}}>{st.bestWinStreak} V</strong></span>
            </div>
          </div>
        </section>

        {/* Fenêtre d'analyse + chargement de l'historique */}
        <section className="card range-bar" style={{padding:"14px 18px", marginBottom: 28}}>
          <div className="row" style={{gap:10, flexWrap:"wrap"}}>
            <span className="muted" style={{fontSize:12,fontWeight:600}}>Analyser</span>
            <div className="seg">
              {RANGES.map((r) => (
                <button key={r.id} className={range === r.id ? "active" : ""} onClick={() => setRange(r.id)}
                        disabled={r.id !== 'all' && total < Number(r.id) && total < 20}>
                  {r.id === 'all' ? `Tout (${total})` : r.label}
                </button>
              ))}
            </div>
            <div className="seg">
              {QUEUE_GROUPS.map((g) => <button key={g.id} className={queueGroup === g.id ? "active" : ""} onClick={() => setQueueGroup(g.id)}>{g.label}</button>)}
            </div>
          </div>
          <HistoryProgress job={job} total={total} onExtend={extend}/>
          <style>{`.range-bar { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; justify-content: space-between; }`}</style>
        </section>

        <nav className="tabs">
          {[
            { id:"overview", label:"Vue d'ensemble" },
            { id:"history", label:`Historique (${sel.length})` },
            { id:"champions", label:"Champions joués" },
            { id:"graphs", label:"Statistiques" },
          ].map(t => (
            <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </nav>

        <div key={tab} className="anim-fade-in">
          {tab === "overview"  && <PfOverview s={s} st={st} sel={sel} open={open} setRoute={setRoute} goHistory={() => setTab("history")}/>}
          {tab === "history"   && <PfHistory sel={sel} open={open}/>}
          {tab === "champions" && <PfChampions st={st} setRoute={setRoute}/>}
          {tab === "graphs"    && <PfGraphs st={st}/>}
        </div>
      </div>
    </div>
  );
}

// Progression du chargement de l'historique (job de fond côté API) + bouton d'extension
function HistoryProgress({ job, total, onExtend }) {
  const running = job && (job.status === 'running' || job.status === 'queued');
  if (running) {
    const tgt = job.target || 100;
    const done = Math.min(tgt, Math.max(job.stored || 0, total));
    const p = Math.min(100, Math.round((done / tgt) * 100));
    return (
      <div style={{minWidth:240}}>
        <div className="row-bw" style={{fontSize:12, marginBottom:6}}>
          <span className="muted"><span className="btr-spin" style={{display:"inline-block",width:10,height:10,borderRadius:"50%",border:"2px solid var(--surface-3)",borderTopColor:"var(--accent)",marginRight:8,verticalAlign:"middle"}}/>Chargement de l'historique Riot…</span>
          <span className="mono muted">{done}/{tgt}</span>
        </div>
        <div style={{height:6,borderRadius:999,background:"var(--surface-3)",overflow:"hidden"}}><div style={{width:`${p}%`,height:"100%",background:"var(--accent)",transition:"width .4s"}}/></div>
        <style>{`@keyframes btrspin{to{transform:rotate(360deg)}} .btr-spin{animation:btrspin .7s linear infinite}`}</style>
      </div>
    );
  }
  if (job?.status === 'error') {
    return <div className="row" style={{gap:10,fontSize:12}}><span className="t-loss">{job.error || "Chargement interrompu."}</span><button className="btn sm outline" onClick={() => onExtend(Math.max(100, total + 100))}>Réessayer</button></div>;
  }
  const exhausted = !!job?.exhausted;
  const next = total < 100 ? 100 : total < 300 ? 300 : total < 500 ? 500 : null;
  return (
    <div className="row" style={{gap:10, fontSize:12, alignItems:"center"}}>
      <span className="muted">{total} partie{total > 1 ? "s" : ""} en mémoire{exhausted ? " · tout l'historique Riot disponible est chargé" : ""}</span>
      {!exhausted && next && (
        <button className="btn sm outline" onClick={() => onExtend(next)}>
          <Icon name="download" size={12}/> Analyser jusqu'à {next} parties
        </button>
      )}
    </div>
  );
}

function RankCard({ kind, data }) {
  if (!data) {
    return (
      <div className="card" style={{display:"flex",gap:18,alignItems:"center"}}>
        <RankCrest tier="Fer" division="" size={64}/>
        <div className="grow"><div className="muted" style={{fontSize:12,fontWeight:600,marginBottom:4}}>{kind}</div>
          <div className="h4" style={{fontSize:20}}>Non classé</div></div>
      </div>
    );
  }
  const total = (data.wins || 0) + (data.losses || 0);
  const wr = total ? ((data.wins / total) * 100).toFixed(1) : "0.0";
  return (
    <div className="card" style={{display:"flex",gap:18,alignItems:"center"}}>
      <RankCrest tier={tierFr(data.tier)} division={data.rank} size={64}/>
      <div className="grow">
        <div className="muted" style={{fontSize:12,fontWeight:600,marginBottom:4}}>{kind}</div>
        <div className="h4" style={{fontSize:20}}>{tierFr(data.tier)} <span style={{color:"var(--ink-3)",fontWeight:500}}>{data.rank}</span></div>
        <div className="muted" style={{fontSize:13,marginTop:4}}>
          <strong className="t-accent">{data.lp} LP</strong> · {data.wins}V — {data.losses}D · <strong style={{color: wr>50?"var(--win)":"var(--loss)"}}>{wr}%</strong>
        </div>
      </div>
    </div>
  );
}

function StatGrid({ st }) {
  const cells = [
    { v: `${st.winrate}%`, l: "Winrate", sub: `${st.wins}V — ${st.losses}D`, color: st.winrate >= 50 ? "var(--win)" : "var(--loss)" },
    { v: String(st.kda), l: "KDA", sub: `${st.avgK} / ${st.avgD} / ${st.avgA}`, color: "var(--accent)" },
    { v: st.farmGames ? String(st.csMin) : "—", l: "CS / minute", sub: st.farmGames && st.farmGames < st.games ? `hors Arena (${st.farmGames} parties)` : null },
    { v: st.kp != null ? `${st.kp}%` : "—", l: "Participation aux kills" },
    { v: fmtCompact(st.dmgMin), l: "Dégâts / minute", sub: st.dmgShare != null ? `${st.dmgShare}% des dégâts de l'équipe` : null },
    { v: String(st.visionMin), l: "Vision / minute" },
    { v: fmtCompact(st.goldMin), l: "Or / minute" },
    { v: fmtMinutes(st.avgDuration), l: "Durée moyenne", sub: st.games ? `V ${fmtMinutes(st.durWin)} · D ${fmtMinutes(st.durLoss)}` : null },
  ];
  return (
    <div className="g-2" style={{marginTop:20, gap: 16}}>
      {cells.map((c) => <StatStrip key={c.l} value={c.v} label={c.l} sub={c.sub} color={c.color}/>)}
    </div>
  );
}

function PfOverview({ s, st, sel, open, setRoute, goHistory }) {
  const recent = sel.slice(0, 10);
  return (
    <div className="g-2" style={{alignItems:"start"}}>
      <section>
        <SecHead title="Parties récentes" meta={`${recent.length} dernières — clique pour ouvrir`}
                 action={sel.length > 10 ? <button className="btn ghost sm" onClick={goHistory}>Tout l'historique <Icon name="arrowR" size={14}/></button> : null}/>
        {recent.length === 0 ? <EmptyState title="Aucune partie" hint="Riot ne renvoie aucune partie récente pour ce compte dans cette file."/> : (
          <div className="stack-sm">{recent.map(m => <MatchRow key={m.id} match={m} onClick={() => open(m)}/>)}</div>
        )}
      </section>

      <aside className="stack">
        <div className="card">
          <SecHead title="Statistiques" meta={`${st.games} partie${st.games > 1 ? "s" : ""}${st.remakes ? ` · ${st.remakes} remake${st.remakes > 1 ? "s" : ""} ignoré${st.remakes > 1 ? "s" : ""}` : ""}${st.oldest ? ` · depuis le ${fmtDateShort(st.oldest)}` : ""}`}/>
          <FormStrip results={st.form.length ? st.form : [false]}/>
          <StatGrid st={st}/>
        </div>

        <div className="card">
          <SecHead title="Champions favoris" meta={st.bestChamp ? `Meilleur winrate (5 parties min.) : ${st.bestChamp.name} · ${st.bestChamp.winrate}%` : null}/>
          <div className="stack-sm">
            {st.byChamp.slice(0, 5).map((c,i) => (
              <button key={c.id} className="fav-row" onClick={() => setRoute("champion", { champId: c.id })}>
                <span style={{fontFamily:"var(--font-display)",fontWeight:800,fontSize:14,color:"var(--ink-3)",width:20}}>{i+1}</span>
                <ChampImg id={c.id} size="sm"/>
                <div className="grow">
                  <div style={{fontWeight:700,fontSize:14}}>{c.name}</div>
                  <div className="muted" style={{fontSize:12}}>{c.games} partie{c.games > 1 ? "s" : ""} · <span style={{color: c.winrate >= 50 ? "var(--win)" : "var(--loss)", fontWeight:600}}>{c.winrate}% WR</span>{c.kp != null ? ` · KP ${c.kp}%` : ""}</div>
                </div>
                <span className="tag accent">KDA {c.kda}</span>
              </button>
            ))}
            {!st.byChamp.length && <EmptyState title="Aucun champion"/>}
          </div>
          <style>{`.fav-row { display: flex; align-items: center; gap: 12px; padding: 8px; border-radius: 10px; width: 100%; text-align: left; transition: background .12s; } .fav-row:hover { background: var(--surface-2); }`}</style>
        </div>

        {s.mastery?.length > 0 && (
          <div className="card">
            <SecHead title="Maîtrises" meta="points de maîtrise"/>
            <div className="stack-sm">
              {s.mastery.slice(0, 5).map((m) => {
                const dd = getChamp(m.championId);
                if (!dd) return null;
                return (
                  <div key={m.championId} className="row-tight" style={{gap:12,padding:"4px 0"}}>
                    <ChampImg id={dd.id} size="sm"/>
                    <div className="grow">
                      <div style={{fontWeight:700,fontSize:14}}>{dd.name}</div>
                      <div className="muted" style={{fontSize:12}}>Niveau {m.level}</div>
                    </div>
                    <span className="tag accent mono">{fmtCompact(m.points)} pts</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="card">
          <SecHead title="Rôles joués"/>
          <div className="stack-sm">
            {st.byRole.map(x => (
              <div key={x.role}>
                <div className="row-bw" style={{fontSize:13,marginBottom:5}}>
                  <span style={{fontWeight:600}}>{x.label}</span>
                  <span style={{color:"var(--ink-3)"}}>{x.games} parties · {x.pct}% · <strong style={{color: x.winrate>=50?"var(--win)":"var(--loss)"}}>{x.winrate}% WR</strong></span>
                </div>
                <Meter value={x.pct} kind={x.winrate>=50?"win":"loss"}/>
              </div>
            ))}
            {!st.byRole.length && <span className="muted" style={{fontSize:13}}>Pas de rôle identifiable (ARAM, Arena…).</span>}
          </div>
        </div>

        <div className="card">
          <SecHead title="Files jouées"/>
          <div className="stack-sm">
            {st.byQueue.map(q => (
              <div key={q.queue} className="row-bw" style={{fontSize:13}}>
                <span style={{fontWeight:600}}>{q.queue}</span>
                <span className="muted">{q.games} partie{q.games > 1 ? "s" : ""} · <strong style={{color: q.winrate>=50?"var(--win)":"var(--loss)"}}>{q.winrate}%</strong></span>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

function MatchRow({ match, onClick }) {
  const m = match;
  return (
    <button className={"match " + (m.win ? "win" : "loss")} onClick={onClick}>
      <div>
        <span className={"res-pill " + (m.win ? "win" : "loss")}>{m.remake ? "Remake" : m.win ? "Victoire" : "Défaite"}</span>
        <div className="muted" style={{fontSize:11,marginTop:6}}>{m.duration}</div>
      </div>
      <div style={{position:"relative"}}>
        <ChampImg id={m.champion} size="md"/>
        <div style={{display:"flex",flexDirection:"column",gap:2,position:"absolute",right:-22,top:2}}>
          <SpellById id={m.summoner1Id} size={20}/>
          <SpellById id={m.summoner2Id} size={20}/>
        </div>
      </div>
      <div style={{marginLeft:20}}>
        <div style={{fontWeight:700,fontSize:14}}>{m.queue}</div>
        <div className="muted" style={{fontSize:12,marginTop:2}}>{m.when} · CS {m.cs} ({m.csPerMin})</div>
        <div className="row-tight" style={{gap:4,marginTop:6,flexWrap:"wrap"}}>
          <span className="tag">{m.roleLabel}</span>
          {m.kp != null && <span className="tag" title="Participation aux kills">KP {m.kp}%</span>}
          {m.multikill >= 3 && <span className="tag gold">{m.multikill >= 5 ? "Penta" : m.multikill === 4 ? "Quadra" : "Triple"}</span>}
        </div>
      </div>
      <div className="center"><KDA k={m.kda[0]} d={m.kda[1]} a={m.kda[2]}/></div>
      <div className="hide-sm"><ItemsRow ids={m.items} size={28}/></div>
      <div className="hide-sm">
        <div className="muted" style={{fontSize:11,marginBottom:4}}>Dégâts</div>
        <div className="mono" style={{fontSize:13,fontWeight:600}}>{fmtCompact(m.damage)}</div>
      </div>
      <div className="hide-sm"><Icon name="chevR" size={16} style={{color:"var(--ink-3)"}}/></div>
    </button>
  );
}

function PfHistory({ sel, open }) {
  const [shown, setShown] = useState(20);
  if (sel.length === 0) return <EmptyState title="Aucune partie" hint="Aucune partie dans cette fenêtre ou cette file."/>;
  return (
    <div>
      <div className="stack-sm">{sel.slice(0, shown).map((m,i) => <MatchRow key={m.id+i} match={m} onClick={() => open(m)}/>)}</div>
      {shown < sel.length && (
        <div style={{textAlign:"center",marginTop:18}}>
          <button className="btn outline" onClick={() => setShown(n => n + 20)}>
            Voir plus ({sel.length - shown} restantes)
          </button>
        </div>
      )}
    </div>
  );
}

function PfChampions({ st, setRoute }) {
  const [shown, setShown] = useState(30);
  if (!st.byChamp.length) return <EmptyState title="Aucun champion"/>;
  return (
    <div>
    <div className="card card-flush" style={{overflowX:"auto"}}>
      <table className="tbl">
        <thead><tr><th style={{width:32}}>#</th><th>Champion</th><th className="num">Parties</th><th>Winrate</th><th className="num">KDA</th><th className="num">K / D / A</th><th className="num">CS/min</th><th className="num">KP</th><th className="num">Dégâts/min</th></tr></thead>
        <tbody>
          {st.byChamp.slice(0, shown).map((c,i) => (
            <tr key={c.id} style={{cursor:"pointer"}} onClick={() => setRoute("champion", { champId: c.id })}>
              <td className="muted">{i+1}</td>
              <td><div className="row-tight"><ChampImg id={c.id} size="sm"/><div><div style={{fontWeight:700}}>{c.name}</div><div className="muted" style={{fontSize:11,fontStyle:"italic"}}>{c.title}</div></div></div></td>
              <td className="num mono">{c.games}</td>
              <td style={{minWidth:160}}>
                <div className="row-bw" style={{marginBottom:4}}>
                  <span className="mono" style={{color:c.winrate>=50?"var(--win)":"var(--loss)",fontWeight:700,fontSize:14}}>{c.winrate}%</span>
                  <span className="muted mono" style={{fontSize:11}}>{c.wins}V/{c.losses}D</span>
                </div>
                <Meter value={c.winrate} kind={c.winrate>=50?"win":"loss"}/>
              </td>
              <td className="num mono t-accent" style={{fontWeight:700}}>{c.kda}</td>
              <td className="num mono muted">{c.avgK} / {c.avgD} / {c.avgA}</td>
              <td className="num mono">{c.csMin != null ? c.csMin : "—"}</td>
              <td className="num mono">{c.kp != null ? `${c.kp}%` : "—"}</td>
              <td className="num mono">{fmtCompact(c.dmgMin)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {shown < st.byChamp.length && (
      <div style={{textAlign:"center",marginTop:18}}>
        <button className="btn outline" onClick={() => setShown((n) => n + 30)}>Voir plus ({st.byChamp.length - shown} champions restants)</button>
      </div>
    )}
    </div>
  );
}

function Bars({ items, labelKey = "label" }) {
  const max = Math.max(1, ...items.map((x) => x.games));
  return (
    <div style={{display:"grid",gridTemplateColumns:`repeat(${items.length},1fr)`,gap:6,alignItems:"end",height:130,marginTop:8}}>
      {items.map((x) => (
        <div key={x[labelKey]} title={`${x.games} parties · ${x.winrate}% de victoires`} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,height:"100%",justifyContent:"flex-end"}}>
          <span className="mono" style={{fontSize:10,color:"var(--ink-3)"}}>{x.games ? `${x.winrate}%` : ""}</span>
          <div style={{width:"100%",height:`${(x.games/max)*100}%`,minHeight:x.games?4:0,background: x.games ? (x.winrate>=50?"var(--win)":"var(--loss)") : "var(--surface-3)",borderRadius:4,opacity:x.games?0.9:0.3}}/>
          <span className="mono" style={{fontSize:10,color:"var(--ink-mute)"}}>{x[labelKey]}</span>
        </div>
      ))}
    </div>
  );
}

function PfGraphs({ st }) {
  const maxH = Math.max(1, ...st.byHour.map((h) => h.games));
  return (
    <div className="g-2">
      <div className="card">
        <SecHead title="Winrate cumulé" meta={`${st.games} parties, de la plus ancienne à la plus récente`}/>
        {st.cumul.length > 1 ? <LineChart values={st.cumul} accent={st.cumul[st.cumul.length-1] >= 50 ? "win" : "loss"} startLabel={fmtDateShort(st.oldest)} endLabel={fmtDateShort(st.newest)}/> : <EmptyState title="Pas assez de parties"/>}
      </div>
      <div className="card">
        <SecHead title="Performance par rôle"/>
        <div className="row" style={{justifyContent:"space-around",padding:"16px 0",flexWrap:"wrap",gap:20}}>
          {st.byRole.map(x => <RolePerf key={x.role} role={`${x.label} · ${x.games}`} value={x.winrate}/>)}
          {!st.byRole.length && <span className="muted" style={{fontSize:13}}>Pas de rôle identifiable.</span>}
        </div>
      </div>
      <div className="card">
        <SecHead title="Jours de la semaine" meta="parties et winrate par jour"/>
        <Bars items={st.byDay}/>
      </div>
      <div className="card">
        <SecHead title="Files" meta="répartition des parties"/>
        <div className="stack-sm" style={{marginTop:8}}>
          {st.byQueue.map((q) => (
            <div key={q.queue}>
              <div className="row-bw" style={{fontSize:13,marginBottom:5}}>
                <span style={{fontWeight:600}}>{q.queue}</span>
                <span className="muted">{q.games} · <strong style={{color:q.winrate>=50?"var(--win)":"var(--loss)"}}>{q.winrate}%</strong></span>
              </div>
              <Meter value={(q.games / Math.max(1, st.games)) * 100} kind={q.winrate>=50?"win":"loss"}/>
            </div>
          ))}
        </div>
      </div>
      <div className="card" style={{gridColumn:"1 / -1"}}>
        <SecHead title="Heures d'activité" meta="parties par heure de la journée"/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(24,1fr)",gap:4,alignItems:"end",height:120,marginTop:8}}>
          {st.byHour.map((x) => (
            <div key={x.hour} title={`${x.hour}h : ${x.games} parties`} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,height:"100%",justifyContent:"flex-end"}}>
              <div style={{width:"100%",height:`${(x.games/maxH)*100}%`,minHeight:x.games?4:0,background:"var(--accent)",borderRadius:3,opacity:x.games?1:0.15}}/>
              <span className="mono" style={{fontSize:9,color:"var(--ink-mute)"}}>{x.hour%3===0?x.hour:""}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <SecHead title="Multi-kills" meta="plus grand multi-kill par partie"/>
        <div className="g-4" style={{gap:12}}>
          {[["Double", st.multikills.double], ["Triple", st.multikills.triple], ["Quadra", st.multikills.quadra], ["Penta", st.multikills.penta]].map(([l, v]) => (
            <div key={l}><div className="bignum" style={{fontSize:28, color: v ? "var(--accent)" : "var(--ink-mute)"}}>{v}</div><div className="muted" style={{fontSize:12}}>{l}</div></div>
          ))}
        </div>
      </div>
      <div className="card">
        <SecHead title="Séries et durées"/>
        <div className="g-2" style={{gap:12}}>
          <div><div className="bignum" style={{fontSize:28, color: st.streak.type === 'win' ? "var(--win)" : "var(--loss)"}}>{st.streak.n ? `${st.streak.n} ${st.streak.type === 'win' ? 'V' : 'D'}` : "—"}</div><div className="muted" style={{fontSize:12}}>Série en cours</div></div>
          <div><div className="bignum t-win" style={{fontSize:28}}>{st.bestWinStreak}</div><div className="muted" style={{fontSize:12}}>Meilleure série de victoires</div></div>
          <div><div className="bignum" style={{fontSize:28}}>{fmtMinutes(st.durWin)}</div><div className="muted" style={{fontSize:12}}>Durée moyenne d'une victoire</div></div>
          <div><div className="bignum" style={{fontSize:28}}>{fmtMinutes(st.durLoss)}</div><div className="muted" style={{fontSize:12}}>Durée moyenne d'une défaite</div></div>
        </div>
      </div>
    </div>
  );
}

function RolePerf({ role, value }) {
  const r = 32, c = 2 * Math.PI * r;
  return (
    <div style={{textAlign:"center"}}>
      <svg width={80} height={80} viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="var(--surface-3)" strokeWidth="4"/>
        <circle cx="40" cy="40" r={r} fill="none" stroke={value>=50?"var(--win)":"var(--loss)"} strokeWidth="4"
                strokeDasharray={c} strokeDashoffset={c * (1 - value/100)} transform="rotate(-90 40 40)" strokeLinecap="round"/>
        <text x="40" y="45" textAnchor="middle" style={{fontFamily:"var(--font-display)",fontSize:18,fontWeight:800,fill:"var(--ink)",letterSpacing:"-0.03em"}}>{Math.round(value)}%</text>
      </svg>
      <div style={{fontSize:12,color:"var(--ink-3)",marginTop:6,fontWeight:600}}>{role}</div>
    </div>
  );
}

// ============================================================
// MATCH — Détail
// ============================================================
function MatchScreen({ setRoute, nav }) {
  const matchId = nav?.matchId;
  const state = useApi(matchId ? `match-${matchId}` : null, () => btrApi.match(matchId));

  // Runes par joueur (les `perks` de /api/match ne passent pas par l'adaptateur).
  const perksByPuuid = useMemo(() => {
    const out = {};
    (state.data?.teams || []).forEach((team) => (team.players || []).forEach((p) => {
      if (p.perks) out[p.puuid] = p.perks;
    }));
    return out;
  }, [state.data]);

  if (!matchId) return <div className="container"><EmptyState title="Aucune partie sélectionnée" hint="Ouvre une partie depuis un profil."/>
    <div style={{textAlign:"center",marginTop:16}}><button className="btn outline" onClick={() => setRoute("profile")}>Aller au profil</button></div></div>;
  if (state.loading && !state.data) return <div className="container"><Spinner label="Chargement de la partie…"/></div>;
  if (state.error) return <div className="container"><ErrorState error={state.error} onRetry={state.refetch}/></div>;

  const m = A.adaptMatch(state.data, nav?.summoner?.puuid);
  if (!m.blueTeam || !m.redTeam) return <div className="container"><EmptyState title="Partie indisponible"/></div>;
  const me = m.blueTeam.players.find(p => p.isMe) || m.redTeam.players.find(p => p.isMe) || m.blueTeam.players[0];
  const myTeam = m.blueTeam.players.includes(me) ? m.blueTeam : m.redTeam;

  return (
    <div className="anim-fade-in">
      <div className="container container-fluid" style={{padding:"20px 28px 0"}}>
        <button className="btn ghost sm" onClick={() => setRoute("profile")} style={{marginBottom:14}}><Icon name="arrowL" size={14}/> Retour au profil</button>
        <section className="hero" style={{minHeight: 360, padding: 36}}>
          <div className="hero-bg"><img src={champSplash(me.champ, 0)} alt=""/></div>
          <div className="hero-content" style={{maxWidth: 900, display:"flex", gap:32, alignItems:"flex-end", flexWrap:"wrap"}}>
            <div className="grow">
              <div className="row" style={{gap:10,marginBottom:16}}>
                <span className="tag accent">{m.queue}</span>
                <span className="tag" style={{background:"rgba(255,255,255,0.14)",color:"#fff"}}>{m.duration}</span>
                <span className="tag" style={{background:"rgba(255,255,255,0.14)",color:"#fff"}}>{m.when}</span>
              </div>
              <h1 className="h1" style={{color:"#fff",fontSize:"clamp(36px,5vw,56px)"}}>
                {myTeam.win ? "Victoire" : "Défaite"} avec <span style={{color:"var(--accent)"}}>{(getChamp(me.champ)||{}).name || me.champ}</span>
              </h1>
              <p style={{color:"rgba(255,255,255,0.78)",fontSize:16,marginTop:14, maxWidth: 560}}>
                {me.sum} · {laneLabel(me.role)} · {me.kda[0]}/{me.kda[1]}/{me.kda[2]} · {me.cs} CS · {fmtCompact(me.dmg)} dégâts.
              </p>
            </div>
            <div className="match-score">
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:6}}>Bleus</div>
                <div className="bignum" style={{fontSize:64,color:"var(--win)"}}>{m.blueTeam.objectives.kill}</div>
              </div>
              <div style={{color:"rgba(255,255,255,0.4)",fontSize:32,padding:"0 16px"}}>—</div>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:6}}>Rouges</div>
                <div className="bignum" style={{fontSize:64,color:"var(--loss)"}}>{m.redTeam.objectives.kill}</div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="container">
        <div className="anim-fade-in stack">
          <TeamTable team={m.blueTeam} side="Bleue" perksByPuuid={perksByPuuid}/>
          <ObjectivesStrip blue={m.blueTeam.objectives} red={m.redTeam.objectives}/>
          <TeamTable team={m.redTeam} side="Rouge" perksByPuuid={perksByPuuid}/>
          <MatchTimelinePanel matchId={matchId} match={m}/>
          {isSummonersRift(state.data) && <MatchReplayPanel matchId={matchId}/>}
        </div>
      </div>

      <style>{`.match-score { display: flex; align-items: center; padding: 18px 24px; background: rgba(0,0,0,0.35); border-radius: 16px; backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.1); }`}</style>
    </div>
  );
}

// ---- Runes d'un joueur : keystone + voie secondaire (perks de /api/match) ----
function PlayerRunes({ perks }) {
  if (!perks) return null;
  const keystone = getRunePerk(perks.keystone);
  const sub = getRuneStyle(perks.subStyle);
  const primary = getRuneStyle(perks.primaryStyle);
  if (!keystone && !sub) return null;
  return (
    <div className="runes-mini" title={[keystone?.name, primary && sub ? `${primary.name} / ${sub.name}` : sub?.name].filter(Boolean).join(' · ')}>
      {keystone
        ? <img className="rune-keystone" src={runeStyleIcon(keystone.icon)} alt={keystone.name} loading="lazy"/>
        : <span className="rune-keystone"/>}
      {sub && <img className="rune-sub" src={runeStyleIcon(sub.icon)} alt={sub.name} loading="lazy"/>}
    </div>
  );
}

function TeamTable({ team, side, perksByPuuid }) {
  return (
    <section className="card card-flush">
      <div style={{padding:"18px 22px",borderBottom:"1px solid var(--border)"}}>
        <div className="row-bw" style={{flexWrap:"wrap",gap:14}}>
          <div className="row-tight">
            <span style={{width:8,height:24,borderRadius:2,background: team.win ? "var(--win)" : "var(--loss)"}}/>
            <div>
              <div className="h4" style={{color: team.win ? "var(--win)" : "var(--loss)"}}>Équipe {side} · {team.win ? "Victoire" : "Défaite"}</div>
              <div className="muted" style={{fontSize:12,marginTop:2}}>
                {team.objectives.tower} tours · {team.objectives.dragon} drakes · {team.objectives.baron} baron · {(team.gold/1000).toFixed(1)}K or
              </div>
            </div>
          </div>
        </div>
      </div>
      <table className="tbl">
        <thead><tr><th>Joueur</th><th>KDA</th><th className="num">CS</th><th>Dégâts</th><th className="num">Vision</th><th className="num">Or</th><th>Items</th></tr></thead>
        <tbody>
          {team.players.map((p, i) => (
            <tr key={i} className={p.isMe ? "me" : ""}>
              <td>
                <div className="row-tight">
                  <div style={{position:"relative"}}>
                    <ChampImg id={p.champ} size="sm"/>
                    {p.level ? <span style={{position:"absolute",bottom:-3,right:-3,background:"var(--ink)",color:"var(--bg)",padding:"1px 5px",fontSize:10,fontWeight:800,borderRadius:6,fontFamily:"var(--font-mono)"}}>{p.level}</span> : null}
                  </div>
                  <PlayerRunes perks={perksByPuuid?.[p.puuid]}/>
                  <div>
                    <div style={{fontWeight:p.isMe?800:600,fontSize:14}}>{p.sum}{p.isMe && <span className="tag accent" style={{marginLeft:8,fontSize:10}}>VOUS</span>}</div>
                    <div className="muted" style={{fontSize:11,fontStyle:"italic"}}>{(getChamp(p.champ)||{}).name || p.champ} · {laneLabel(p.role)}</div>
                  </div>
                </div>
              </td>
              <td><KDA k={p.kda[0]} d={p.kda[1]} a={p.kda[2]} showRatio={false}/></td>
              <td className="num mono">{p.cs}</td>
              <td style={{minWidth:140}}>
                <div className="row-bw" style={{marginBottom:4}}><span className="num mono" style={{fontSize:12,fontWeight:600}}>{fmtCompact(p.dmg)}</span></div>
                <Meter value={(p.dmg/40000)*100} kind={team.win ? "win" : "loss"}/>
              </td>
              <td className="num mono">{p.vis}</td>
              <td className="num mono t-gold">{(p.gold/1000).toFixed(1)}K</td>
              <td><ItemsRow ids={p.items} size={26}/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ---- Timeline : courbe d'écart d'or + moments clés (Riot live, absent sur vieux matchs) ----
const fmtClock = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const MONSTER_LABELS = {
  DRAGON: 'Drake', RIFTHERALD: 'Héraut', BARON_NASHOR: 'Nashor', HORDE: 'Larves', ATAKHAN: 'Atakhan',
};

function GoldDiffChart({ frames }) {
  const W = 600, H = 160, PAD = 6;
  const diffs = frames.map((f) => (f.gold[100] || 0) - (f.gold[200] || 0));
  const maxAbs = Math.max(1000, ...diffs.map(Math.abs));
  const x = (i) => PAD + (i / Math.max(1, frames.length - 1)) * (W - 2 * PAD);
  const y = (d) => H / 2 - (d / maxAbs) * (H / 2 - PAD);
  const path = diffs.map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(d).toFixed(1)}`).join(' ');
  const last = diffs[diffs.length - 1] || 0;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}>
        <line x1={PAD} y1={H/2} x2={W-PAD} y2={H/2} stroke="var(--border)" strokeDasharray="4 4"/>
        <path d={`${path} L${x(diffs.length-1)},${H/2} L${x(0)},${H/2} Z`} fill={last >= 0 ? "var(--win)" : "var(--loss)"} opacity="0.08"/>
        <path d={path} fill="none" stroke={last >= 0 ? "var(--win)" : "var(--loss)"} strokeWidth="2.5" strokeLinejoin="round"/>
      </svg>
      <div className="row-bw" style={{fontSize:11,marginTop:6}}>
        <span className="t-win" style={{fontWeight:600}}>▲ Avance bleue</span>
        <span className="muted mono">{last >= 0 ? '+' : ''}{(last/1000).toFixed(1)}K or à la fin</span>
        <span className="t-loss" style={{fontWeight:600}}>▼ Avance rouge</span>
      </div>
    </div>
  );
}

function MatchTimelinePanel({ matchId, match }) {
  const state = useApi(matchId ? `tl-${matchId}` : null, () => btrApi.matchTimeline(matchId));
  if (state.loading && !state.data) return <div className="card"><Spinner label="Chargement de la timeline…"/></div>;
  // Matchs trop anciens : la timeline Riot n'existe plus, on masque le panneau.
  if (state.error || !state.data) return null;
  const tl = state.data;

  const players = [...(match.blueTeam?.players || []), ...(match.redTeam?.players || [])];
  const byPuuid = Object.fromEntries(players.map((p) => [p.puuid, p]));
  const champOf = (pid) => (pid >= 1 && byPuuid[tl.participants[pid - 1]]) || null;

  const kills = tl.events.filter((e) => e.type === 'CHAMPION_KILL');
  const keyEvents = [
    ...(kills.length ? [{ ...kills[0], firstBlood: true }] : []),
    ...tl.events.filter((e) => e.type !== 'CHAMPION_KILL'),
  ].sort((a, b) => a.t - b.t);

  return (
    <section className="card">
      <SecHead title="Timeline de la partie" meta="écart d'or et objectifs"/>
      {tl.frames.length > 2 && <GoldDiffChart frames={tl.frames}/>}
      {keyEvents.length > 0 && (
        <div style={{marginTop:18}}>
          <div className="muted" style={{fontSize:12,fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:10}}>Moments clés</div>
          <div className="row" style={{gap:8,flexWrap:"wrap"}}>
            {keyEvents.map((e, i) => {
              const blue = (e.teamId === 100) !== (e.type === 'BUILDING_KILL'); // building détruit par l'équipe adverse
              const killer = champOf(e.killerId);
              let label;
              if (e.firstBlood) label = 'Premier sang';
              else if (e.type === 'ELITE_MONSTER_KILL') label = MONSTER_LABELS[e.monsterType] || 'Monstre';
              else label = e.buildingType === 'INHIBITOR_BUILDING' ? 'Inhibiteur' : 'Tour';
              return (
                <span key={i} className="tag" style={{
                  display:"inline-flex",alignItems:"center",gap:6,
                  borderLeft:`3px solid ${blue ? 'var(--win)' : 'var(--loss)'}`,
                }}>
                  <span className="mono muted" style={{fontSize:10}}>{fmtClock(e.t)}</span>
                  {killer && <ChampImg id={killer.champ} size="xs"/>}
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

// ---- Replay 2D : chargé à la demande (l'appel timeline de /api/paths est coûteux) ----
// Affiché uniquement sur la Faille de l'invocateur (voir isSummonersRift).
function MatchReplayPanel({ matchId }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card">
      <SecHead title="Replay 2D" meta="trajectoires des 10 joueurs reconstruites depuis la timeline Riot"
               action={!open ? (
                 <button className="btn primary" onClick={() => setOpen(true)}>
                   <Icon name="play" size={14}/> Charger le replay
                 </button>
               ) : null}/>
      {open ? (
        <MatchReplay matchId={matchId}/>
      ) : (
        <p className="muted" style={{fontSize:13,margin:0}}>
          Rejoue la partie sur la minimap : déplacements minute par minute, kills,
          objectifs, tours et rappels. Le chargement interroge la timeline Riot.
        </p>
      )}
    </section>
  );
}

function ObjectivesStrip({ blue, red }) {
  const Bar = ({ a, b, label }) => {
    const tot = a + b || 1;
    return (
      <div>
        <div className="row-bw" style={{fontSize:13,marginBottom:6}}>
          <span className="t-win mono" style={{fontWeight:700}}>{a}</span>
          <span className="muted" style={{fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",fontSize:11}}>{label}</span>
          <span className="t-loss mono" style={{fontWeight:700}}>{b}</span>
        </div>
        <div style={{display:"flex",height:6,borderRadius:999,overflow:"hidden",background:"var(--surface-3)"}}>
          <div style={{width: (a/tot)*100 + "%", background: "var(--win)"}}/>
          <div style={{width: (b/tot)*100 + "%", background: "var(--loss)"}}/>
        </div>
      </div>
    );
  };
  return (
    <div className="card" style={{padding:20}}>
      <div className="g-3" style={{gap: 28}}>
        <Bar a={blue.kill} b={red.kill} label="Kills"/>
        <Bar a={blue.tower} b={red.tower} label="Tours"/>
        <Bar a={blue.dragon} b={red.dragon} label="Drakes"/>
      </div>
    </div>
  );
}

export {
  HomeScreen, ProfileScreen, MatchScreen, MatchRow,
};
